import React, { useMemo } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, ColGroupDef, themeQuartz } from 'ag-grid-community';
import { IEventAKCConformationCount } from '@eagerdog/interfaces';
import { Constants } from '@eagerdog/constants';
import Button from '../Button/Button';

import './Counts.scss';

interface IProps {
  onClose: () => void;
  buttonsDisabled?: boolean;
  tableCounts: IEventAKCConformationCount[] | any[];
  variationCounts?: any[];
  isSpecialtyShow: boolean;
  specialtyBreed?: string;
}

// Regular classes that are NOT extras (from Constants.dog_class_element_level)
const REGULAR_CLASSES = [
  Constants.dog_class_element_level.akc_4_6_month_puppy,
  Constants.dog_class_element_level.akc_4_6_month_puppy_exhibitor_only,
  Constants.dog_class_element_level.akc_6_9_month_puppy,
  Constants.dog_class_element_level.akc_9_12_month_puppy,
  Constants.dog_class_element_level.akc_12_18_month_puppy,
  Constants.dog_class_element_level.akc_18_24_month_puppy,
  Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
  Constants.dog_class_element_level.akc_adult_bred_by_exhibitor,
  Constants.dog_class_element_level.akc_amateur_owner_handler,
  Constants.dog_class_element_level.akc_american_bred,
  Constants.dog_class_element_level.akc_open
];

interface ISpecialtyRow {
  section: string;
  className: string;
  count: number;
  isTotal: boolean;
  isSectionHeader: boolean;
  isGrandTotal: boolean;
}

interface IAllBreedRow {
  breed_group: string;
  breed: string;
  variety?: string;
  total: number;
  dogs: number;
  bitches: number;
  extras: number;
}

const AKCCounts: React.FC<IProps> = (props) => {

  // ======================
  // ALL-BREED FORMAT
  // ======================
  
  const allBreedData = useMemo(() => {
    if (props.isSpecialtyShow) return [];
    
    const data: IAllBreedRow[] = (props.tableCounts as IEventAKCConformationCount[]).map(count => ({
      breed_group: count.breed_group || '',
      breed: count.breed || '',
      variety: count.variety || '',
      total: count.total || 0,
      dogs: count.male_count || 0,
      bitches: count.female_count || 0,
      extras: count.veteran_count || 0 // For now, just veteran. Can add more if tracked
    }));
    
    // Sort: alphabetically by group, then alphabetically by breed
    data.sort((a, b) => {
      if (a.breed_group !== b.breed_group) {
        return a.breed_group.localeCompare(b.breed_group);
      }
      return a.breed.localeCompare(b.breed);
    });
    
    return data;
  }, [props.tableCounts, props.isSpecialtyShow]);

  const allBreedTotals = useMemo(() => {
    return allBreedData.reduce((acc, row) => ({
      breed_group: '',
      breed: 'Total',
      variety: '',
      total: acc.total + row.total,
      dogs: acc.dogs + row.dogs,
      bitches: acc.bitches + row.bitches,
      extras: acc.extras + row.extras
    }), {
      breed_group: '',
      breed: 'Total',
      variety: '',
      total: 0,
      dogs: 0,
      bitches: 0,
      extras: 0
    });
  }, [allBreedData]);

  const allBreedColumnDefs: (ColDef | ColGroupDef)[] = useMemo(() => [
    {
      field: 'breed_group',
      headerName: 'Group',
      rowGroup: true,
      hide: true,
      enableRowGroup: true
    },
    {
      field: 'breed',
      headerName: 'Breed / Variety',
      minWidth: 300,
      cellRenderer: 'agGroupCellRenderer',
      valueGetter: (params) => {
        if (params.data) {
          const variety = params.data.variety;
          return variety ? `${params.data.breed} (${variety})` : params.data.breed;
        }
        return '';
      }
    },
    {
      field: 'total',
      headerName: 'Total',
      aggFunc: 'sum',
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'dogs',
      headerName: 'Dogs',
      aggFunc: 'sum',
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'bitches',
      headerName: 'Bitches',
      aggFunc: 'sum',
      width: 120,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    },
    {
      field: 'extras',
      headerName: 'Extras',
      aggFunc: 'sum',
      width: 100,
      cellStyle: { textAlign: 'center' },
      headerClass: 'center-header'
    }
  ], []);

  // ======================
  // SPECIALTY FORMAT
  // ======================
  
  const specialtyData = useMemo(() => {
    if (!props.isSpecialtyShow) return [];
    
    const rows: ISpecialtyRow[] = [];
    const counts = props.tableCounts as any[];
    const variationCounts = props.variationCounts || [];
    
    // Group by sex and class
    const dogClasses: { [key: string]: number } = {};
    const bitchClasses: { [key: string]: number } = {};
    const specialsDogs = { count: 0 };
    const specialsBitches = { count: 0 };
    const extrasDogs: { [key: string]: number } = {};
    const extrasBitches: { [key: string]: number } = {};
    
    // Classes that should be broken out by variation
    const VARIATION_CLASSES = [
      Constants.dog_class_element_level.akc_open,
      Constants.dog_class_element_level.akc_american_bred,
      Constants.dog_class_element_level.akc_under_12_month_bred_by_exhibitor,
      Constants.dog_class_element_level.akc_adult_bred_by_exhibitor
    ];
    
    // First process variation counts (Open, American Bred with Brindle/Fawn/etc)
    variationCounts.forEach(vc => {
      const className = vc.class_level || '';
      const variation = vc.variation || '';
      const count = vc.count || 0;
      const sex = vc.dog_sex;
      
      if (!VARIATION_CLASSES.includes(className) || !variation) return;
      
      const keyWithVariation = `${className} (${variation})`;
      
      if (sex === 'Male') {
        dogClasses[keyWithVariation] = (dogClasses[keyWithVariation] || 0) + count;
      } else if (sex === 'Female') {
        bitchClasses[keyWithVariation] = (bitchClasses[keyWithVariation] || 0) + count;
      }
    });
    
    counts.forEach(count => {
      const className = count.class_level || '';
      const maleCount = count.male_count || 0;
      const femaleCount = count.female_count || 0;
      
      // Skip classes that are broken out by variation
      // (they're already counted from variationCounts above)
      if (VARIATION_CLASSES.includes(className)) {
        // Only add if there are no variation counts for this class
        // (meaning all entries are in the base class without variations)
        const hasVariations = variationCounts.some(vc => vc.class_level === className);
        if (hasVariations) {
          return; // Skip - already counted by variation
        }
      }
      
      // Check if Best of Breed (Specials)
      if (className === Constants.dog_class_element_level.akc_best_of_breed) {
        specialsDogs.count += maleCount;
        specialsBitches.count += femaleCount;
      }
      // Check if regular class
      else if (REGULAR_CLASSES.includes(className)) {
        if (maleCount > 0) {
          dogClasses[className] = (dogClasses[className] || 0) + maleCount;
        }
        if (femaleCount > 0) {
          bitchClasses[className] = (bitchClasses[className] || 0) + femaleCount;
        }
      }
      // Everything else is extras
      else {
        if (maleCount > 0) {
          extrasDogs[className] = (extrasDogs[className] || 0) + maleCount;
        }
        if (femaleCount > 0) {
          extrasBitches[className] = (extrasBitches[className] || 0) + femaleCount;
        }
      }
    });
    
    // Calculate totals
    const dogTotal = Object.values(dogClasses).reduce((sum, val) => sum + val, 0);
    const bitchTotal = Object.values(bitchClasses).reduce((sum, val) => sum + val, 0);
    const specialsTotal = specialsDogs.count + specialsBitches.count;
    const extrasTotal = 
      Object.values(extrasDogs).reduce((sum, val) => sum + val, 0) +
      Object.values(extrasBitches).reduce((sum, val) => sum + val, 0);
    const grandTotal = dogTotal + bitchTotal + specialsTotal + extrasTotal;
    
    // DOG CLASSES section
    rows.push({
      section: 'REGULAR DOG CLASSES',
      className: 'REGULAR DOG CLASSES',
      count: 0,
      isTotal: false,
      isSectionHeader: true,
      isGrandTotal: false
    });
    
    // Sort classes alphabetically (catalog order - for now using alphabetical)
    Object.keys(dogClasses).sort().forEach(className => {
      rows.push({
        section: 'REGULAR DOG CLASSES',
        className,
        count: dogClasses[className],
        isTotal: false,
        isSectionHeader: false,
        isGrandTotal: false
      });
    });
    
    if (dogTotal > 0) {
      rows.push({
        section: 'DOG CLASSES',
        className: 'Regular Class Dog Total',
        count: dogTotal,
        isTotal: true,
        isSectionHeader: false,
        isGrandTotal: false
      });
    }
    
    // BITCH CLASSES section
    rows.push({
      section: 'REGULAR BITCH CLASSES',
      className: 'REGULARBITCH CLASSES',
      count: 0,
      isTotal: false,
      isSectionHeader: true,
      isGrandTotal: false
    });
    
    Object.keys(bitchClasses).sort().forEach(className => {
      rows.push({
        section: 'REGULARBITCH CLASSES',
        className,
        count: bitchClasses[className],
        isTotal: false,
        isSectionHeader: false,
        isGrandTotal: false
      });
    });
    
    if (bitchTotal > 0) {
      rows.push({
        section: 'REGULARBITCH CLASSES',
        className: 'Regular Class Bitch Total',
        count: bitchTotal,
        isTotal: true,
        isSectionHeader: false,
        isGrandTotal: false
      });
    }
    
    // BEST OF BREED (SPECIALS) section
    if (specialsTotal > 0) {
      rows.push({
        section: 'BEST OF BREED',
        className: 'BEST OF BREED (SPECIALS)',
        count: 0,
        isTotal: false,
        isSectionHeader: true,
        isGrandTotal: false
      });
      
      if (specialsDogs.count > 0) {
        rows.push({
          section: 'BEST OF BREED',
          className: 'Best of Breed - Dogs',
          count: specialsDogs.count,
          isTotal: false,
          isSectionHeader: false,
          isGrandTotal: false
        });
      }
      
      if (specialsBitches.count > 0) {
        rows.push({
          section: 'BEST OF BREED',
          className: 'Best of Breed - Bitches',
          count: specialsBitches.count,
          isTotal: false,
          isSectionHeader: false,
          isGrandTotal: false
        });
      }
      
      rows.push({
        section: 'BEST OF BREED',
        className: 'Best of Breed Total',
        count: specialsTotal,
        isTotal: true,
        isSectionHeader: false,
        isGrandTotal: false
      });
    }
    
    // EXTRAS section
    if (extrasTotal > 0) {
      rows.push({
        section: 'EXTRAS',
        className: 'EXTRAS (NON-REGULAR CLASSES)',
        count: 0,
        isTotal: false,
        isSectionHeader: true,
        isGrandTotal: false
      });
      
      Object.keys(extrasDogs).sort().forEach(className => {
        rows.push({
          section: 'EXTRAS',
          className: `${className} - Dogs`,
          count: extrasDogs[className],
          isTotal: false,
          isSectionHeader: false,
          isGrandTotal: false
        });
      });
      
      Object.keys(extrasBitches).sort().forEach(className => {
        rows.push({
          section: 'EXTRAS',
          className: `${className} - Bitches`,
          count: extrasBitches[className],
          isTotal: false,
          isSectionHeader: false,
          isGrandTotal: false
        });
      });
      
      rows.push({
        section: 'EXTRAS',
        className: 'Extras Total',
        count: extrasTotal,
        isTotal: true,
        isSectionHeader: false,
        isGrandTotal: false
      });
    }
    
    // GRAND TOTAL
    rows.push({
      section: 'TOTALS',
      className: 'GRAND TOTAL',
      count: grandTotal,
      isTotal: false,
      isSectionHeader: false,
      isGrandTotal: true
    });
    
    return rows;
  }, [props.tableCounts, props.variationCounts, props.isSpecialtyShow]);

  const specialtyColumnDefs: ColDef[] = useMemo(() => [
    {
      field: 'className',
      headerName: 'Class',
      flex: 1,
      minWidth: 300,
      cellStyle: (params: any) => {
        const baseStyle: any = { textAlign: 'left' };
        if (params.data?.isSectionHeader) {
          return { ...baseStyle, fontWeight: 'bold', fontSize: '14px', backgroundColor: '#e0e0e0' };
        }
        if (params.data?.isTotal) {
          return { ...baseStyle, fontWeight: 'bold', backgroundColor: '#fff3e0' };
        }
        if (params.data?.isGrandTotal) {
          return { ...baseStyle, fontWeight: 'bold', fontSize: '16px', backgroundColor: '#ffb74d' };
        }
        return baseStyle;
      }
    },
    {
      field: 'count',
      headerName: 'Count',
      width: 120,
      cellStyle: (params: any) => {
        const baseStyle: any = { textAlign: 'center' };
        if (params.data?.isSectionHeader) {
          return { ...baseStyle, fontWeight: 'bold', fontSize: '14px', backgroundColor: '#e0e0e0' };
        }
        if (params.data?.isTotal) {
          return { ...baseStyle, fontWeight: 'bold', backgroundColor: '#fff3e0' };
        }
        if (params.data?.isGrandTotal) {
          return { ...baseStyle, fontWeight: 'bold', fontSize: '16px', backgroundColor: '#ffb74d' };
        }
        return baseStyle;
      },
      valueFormatter: (params: any) => {
        if (params.data?.isSectionHeader) {
          return '';
        }
        return params.value?.toString() || '0';
      }
    }
  ], []);

  const defaultColDef = useMemo<ColDef>(() => ({
    sortable: false,
    resizable: false,
    filter: false
  }), []);

  const autoGroupColumnDef = useMemo<ColDef>(() => ({
    headerName: 'Group / Breed',
    width: 300,
    minWidth: 300,
    maxWidth: 300,
    cellRendererParams: {
      suppressCount: true
    }
  }), []);

  const theme = themeQuartz.withParams({
    fontFamily: "Roboto",
    headerFontFamily: "Roboto",
  });

  return (
    <div className="Counts">
      <div className="title">
        {props.isSpecialtyShow 
          ? `Counts - ${props.specialtyBreed || 'Specialty Show'}` 
          : 'Counts - All Breed'}
      </div>
      <p>Below are the counts for this show.</p>
      
      {props.isSpecialtyShow ? (
        // SPECIALTY FORMAT
        <div className="counts-section">
          <div className="counts-grid">
            <AgGridReact
              theme={theme}
              rowData={specialtyData}
              columnDefs={specialtyColumnDefs}
              defaultColDef={defaultColDef}
              animateRows={false}
              domLayout="autoHeight"
              popupParent={document.body}
            />
          </div>
        </div>
      ) : (
        // ALL-BREED FORMAT
        <div className="counts-section">
          <div className="counts-grid">
            <AgGridReact
              theme={theme}
              rowData={allBreedData}
              columnDefs={allBreedColumnDefs}
              defaultColDef={defaultColDef}
              autoGroupColumnDef={autoGroupColumnDef}
              groupDefaultExpanded={-1}
              suppressAggFuncInHeader={true}
              animateRows={false}
              domLayout="autoHeight"
              popupParent={document.body}
              pinnedBottomRowData={[allBreedTotals]}
            />
          </div>
        </div>
      )}
      
      <div className="button-group">
        <Button disabled={props.buttonsDisabled} onClick={props.onClose}>Close</Button>
      </div>
    </div>
  );
};

export default AKCCounts;

